import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { useEffect, useState } from "react";
import { getNavigationTree, type NavigationTreeDataDTO } from "../api/getNavigationTree";
import Spinner from "../../../common/components/Spinner/Spinner";
import { toggleNotifications } from "../api/toggleNotifications";
import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { redirectToUrl } from "../../../utils/url/urlUtils";
import { HOMEPAGE } from "../../../utils/url/staticUrls";
import { NotificationSettingsNavigation } from "./NotificationSettingsNavigation";
import { Box, styled } from "~/common/mui";
import { NotificationSettings } from "~/features/notifications/NotificationSettingsPage/NotificationSettings";
import { useTranslation } from "react-i18next";

const PageGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: '"title" "settings"',
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("tablet")]: {
    gridTemplateAreas: '"title title" "navigation settings"',
    gridTemplateColumns: `calc(25% - var(--gutter) / 2) calc(75% - var(--gutter) / 2)`,
    columnGap: "var(--gutter)",
  },
  [theme.breakpoints.up("desktop-m")]: {
    gridTemplateAreas: '"title title title title" "navigation gap settings gap2"',
    gridTemplateColumns: `calc(3 / 12 * 100% - var(--gutter) / 2) calc(1 / 12 * 100% - var(--gutter)) calc(7 / 12 * 100% - var(--gutter) / 2) calc(1 / 12 * 100% - var(--gutter))`,
  },
}));

const PageTitleArea = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  gridArea: "title",
  [theme.breakpoints.up("tablet")]: {
    marginBottom: theme.spacing(8),
  },
}));

const NavigationArea = styled(Box)(({ theme }) => ({
  gridArea: "navigation",
  alignSelf: "flex-start",
  [theme.breakpoints.down("tablet")]: {
    display: "none",
  },
}));

interface NotificationSettingsPageViewProps {
  data: NavigationTreeDataDTO[];
  loading: boolean;
  onToggle: (isParent: boolean, type: string, state: boolean) => void;
}

export function NotificationSettingsPageView({ data, loading, onToggle }: NotificationSettingsPageViewProps) {
  const { t } = useTranslation();

  return (
    <PageGrid>
      <PageTitleArea>
        <PageTitle title={t("notificationsSettingsPage.title")} />
      </PageTitleArea>

      <NavigationArea>
        <NotificationSettingsNavigation navigation={data} />
      </NavigationArea>

      <Box sx={{ gridArea: "settings" }}>
        {loading && <Spinner className="mb-3" />}

        <NotificationSettings data={data} onToggle={onToggle} />
      </Box>
    </PageGrid>
  );
}

export function NotificationSettingsPage() {
  const canSeeNotifications = userHasAnyPermission(Permission.SEE_NOTIFICATIONS);

  if (!canSeeNotifications) {
    redirectToUrl(HOMEPAGE);
  }

  const [data, setData] = useState<NavigationTreeDataDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const getData = () => {
    getNavigationTree().then((response) => {
      setData(response);
      setLoading(false);
    });
  };

  useEffect(() => {
    setLoading(true);
    getData();
  }, []);

  const onChange = (isParent: boolean, type: string, state: boolean) => {
    const requestData = isParent
      ? {
          parent: type,
          parentEnabled: state,
        }
      : {
          child: type,
          childEnabled: state,
        };

    toggleNotifications(requestData).then((response) => {
      if (response) {
        getData();
      }
    });
  };

  return <NotificationSettingsPageView data={data} loading={loading} onToggle={onChange} />;
}

export function createNotificationSettingsPage() {
  return <NotificationSettingsPage />;
}
